/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createHook,
	createContainer,
	createSubscriber,
	type Action,
	type BoundActions,
	type HookFunction,
	type OverrideContainerComponent,
	type Store as StoreType,
	type StoreActionApi,
	type SubscriberComponent,
} from '@atlassian/react-sweet-state';
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import type { TenantContext } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import { QUICKSTART_UI_STATUS } from '../common/constants.tsx';
import type { TaskList, TaskId } from '../common/types.tsx';
import { actions } from './actions/index.tsx';
import type { Props } from './actions/types.tsx';
import { USER_ROLE } from './model/constants.tsx';
import { initialState } from './model/index.tsx';
import type { HasEverSeenRoute, ProjectState, State } from './model/types.tsx';
import { getQuickstartTypeSelector } from './model/utils.tsx';
import { getLocalStorageItem } from './utils.tsx';

export type Actions = typeof actions;

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { QUICKSTART_UI_STATUS };

export const Store: StoreType<
	State,
	{
		changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
		goBackToPreviousTaskGroup: () => Action<State>;
		setIsOpen: (isOpen: boolean) => Action<State>;
		refreshAppState: ({
			atlassianAccountId,
			cloudId,
			appEditions,
			isAdmin,
			canEditPlans,
			createAnalyticsEvent,
			projectType,
			containerType,
		}: {
			canEditPlans: boolean;
			createAnalyticsEvent: CreateUIAnalyticsEvent;
			projectType?: ProjectType;
			containerType?: 'project' | 'plan';
		} & Pick<
			TenantContext,
			'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
		>) => Action<State, Props>;
		setNudgeStep: (nudge: string) => Action<State>;
		addMountedNudge: (nudge: string) => Action<State>;
		removeMountedNudge: (nudge: string) => Action<State>;
		setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
		setProjectState: (projectState: ProjectState) => Action<State>;
		setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
		forceHideNudge: () => Action<State>;
		setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
		setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
		setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
		openNextTask: () => Action<State>;
		resetIsQuickstartLoaded: () => Action<State>;
		refreshAppStateUiStatusOnly: ({
			taskList,
			containerType,
		}: {
			taskList: TaskList | undefined;
			containerType?: 'project' | 'plan';
		}) => Action<State, Props>;
		nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
		nextSetChecklistItemSeen: (taskId: string) => Action<State>;
		nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
		nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
		nextOpenTask: (
			taskId: string,
			{
				closeNudge,
			}?: {
				closeNudge?: boolean;
			},
		) => Action<State>;
		nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
		nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
		nextGetTasks: (props: {
			projectState: ProjectState | null;
			taskGroupParent: string;
			taskList?: TaskList;
			isEligibleForTailoredOnboardingM2P5?: boolean;
			teamType?: string;
			features: {
				hasBacklog?: boolean;
				hasSprint?: boolean;
				hasSummaryPage?: boolean;
				hasRoadmap?: boolean;
				hasList?: boolean;
				hasCalendar?: boolean;
				hasCode?: boolean;
				hasForms?: boolean;
				hasPages?: boolean;
				hasPlanDependencies?: boolean;
				hasPlanProgram?: boolean;
				hasPlanReleases?: boolean;
				hasPlanTeams?: boolean;
			};
			permissions?: {
				canCreateIssue?: boolean;
			};
			containerType?: 'project' | 'plan';
		}) => Action<State>;
		syncVisibilityWithLocal: () => Action<State>;
		universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
		setContainerType: (containerType: 'project' | 'plan') => Action<State>;
		setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
		setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
		focusQuickstartButton: () => Action<State>;
	},
	unknown
> = createStore<State, Actions>({
	name: 'jsw-onboarding-quickstart',
	initialState,
	actions,
});

export const JswOnboardingSubscriber: SubscriberComponent<
	State,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	Record<any, any>
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
> = createSubscriber<State, Actions, State, Record<any, any>>(Store);

export const useJswOnboarding: HookFunction<
	State,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;

					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store);

export const JswOnboardingContainer: OverrideContainerComponent<{
	isGlobal: boolean;
}> = createContainer<
	State,
	Actions,
	{
		isGlobal: boolean;
	}
>(Store, {});

export const shouldShowNudgeSelector = (
	state: State,
	{
		itemNames,
	}: {
		itemNames: string[];
	},
): boolean => {
	const commonConditionsToShowNudge =
		state.shouldRenderComponent &&
		state.visibility[getQuickstartTypeSelector(state)] === QUICKSTART_UI_STATUS.OPEN &&
		// @ts-expect-error - TS2345 - Argument of type 'string | null' is not assignable to parameter of type 'string'.
		itemNames.includes(state.openTask);

	return commonConditionsToShowNudge && state.hasNudgeActive;
};

export const openTaskSelector = (state: State): string | null => state.openTask;

export const useOpenTask: HookFunction<
	string | null,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: openTaskSelector,
});

// nudge step is used to control which nudge to show in multistep nudges
export const nudgeStepSelector = (state: State): string => state.nudgeStep;

export const useNudgeStep: HookFunction<
	string,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: nudgeStepSelector,
});

export const useQuickstartActions: HookFunction<
	State,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, { selector: null });

export const useShouldShowNudge: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	{
		itemNames: string[];
	}
> = createHook(Store, {
	selector: shouldShowNudgeSelector,
});

export const useInviteToProject: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state: State) =>
		Boolean(state.projectState && state.projectState.canInvitePeopleToProject),
});

export const useShowVideoCards: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state: State) => {
		if (!state.projectState) {
			return false;
		}
		const { isNextGen, userRole, isProjectAdmin } = state.projectState;
		return Boolean(
			isNextGen &&
			(userRole === USER_ROLE.JIRA_ADMIN || userRole === USER_ROLE.SITE_ADMIN || isProjectAdmin),
		);
	},
});

export const useProjectState: HookFunction<
	ProjectState | null,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state: State) => state.projectState,
});

export const useQuickstartUiStatus: HookFunction<
	any, // oxlint-disable-line @typescript-eslint/no-explicit-any
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state: State) => {
		const quickstartType = getQuickstartTypeSelector(state);
		if (state.shouldRenderComponent === false) {
			return getLocalStorageItem(`${quickstartType}.visibility`) ?? QUICKSTART_UI_STATUS.NOT_LOADED;
		}
		return state.visibility[quickstartType];
	},
});

// TODO: KURO-1678, delete and replace external usages with useQuickstartUiStatus
export const useIsLoaded: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state: State) => state.isQuickstartLoaded,
});

export const useIsFocusedOn: HookFunction<
	boolean | undefined,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state: State) => state.isFocusedOn,
});

export const useIsDismissed: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;

			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state: State) => {
		const visibility = state.visibility[getQuickstartTypeSelector(state)];
		return (
			visibility === QUICKSTART_UI_STATUS.DISMISSED ||
			visibility === QUICKSTART_UI_STATUS.NOT_LOADED
		);
	},
});

export const useCompletedTaskList: HookFunction<
	string[],
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state: State) => state.completedTaskList,
});

export const useAllTasksCompleted: HookFunction<
	boolean | null,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state: State) => {
		if (!state.tasks) {
			return null;
		}
		return state.tasks.items.every((task) => state.completedTaskList.includes(task));
	},
});

export const useMountedNudges: HookFunction<
	string[],
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state: State) => state.mountedNudges,
});

export const useHasEverSeen: HookFunction<
	HasEverSeenRoute,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state: State) => state.hasEverSeenRoute,
});

export const useRoadmapState: HookFunction<
	{
		roadmapIsCreateEnabled: boolean;
		roadmapIsListEmpty: boolean;
	},
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state: State) => ({
		roadmapIsCreateEnabled: state.roadmapIsCreateEnabled,
		roadmapIsListEmpty: state.roadmapIsListEmpty,
	}),
});

export const useHasNudgeActive: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state: State) => state.hasNudgeActive,
});

export const useSelectedProjectTemplateKeyFromOnboarding: HookFunction<
	string | undefined,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state: State) => state.selectedProjectTemplateKeyFromOnboarding,
});

export const useQuickstartButtonRef: HookFunction<
	HTMLElement | null,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state: State) => state.quickstartButtonRef,
});

export const useIsOpenedViaButton: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state: State) => state.isOpenedViaButton,
});

export const useContainerType: HookFunction<
	'project' | 'plan' | undefined,
	BoundActions<
		State,
		{
			changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
			goBackToPreviousTaskGroup: () => Action<State>;
			setIsOpen: (isOpen: boolean) => Action<State>;
			refreshAppState: ({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				canEditPlans,
				createAnalyticsEvent,
				projectType,
				containerType,
			}: {
				canEditPlans: boolean;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
				projectType?: ProjectType;
				containerType?: 'project' | 'plan';
			} & Pick<
				TenantContext,
				'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'
			>) => Action<State, Props>;
			setNudgeStep: (nudge: string) => Action<State>;
			addMountedNudge: (nudge: string) => Action<State>;
			removeMountedNudge: (nudge: string) => Action<State>;
			setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
			setProjectState: (projectState: ProjectState) => Action<State>;
			setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
			forceHideNudge: () => Action<State>;
			setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
			setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
			setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
			openNextTask: () => Action<State>;
			resetIsQuickstartLoaded: () => Action<State>;
			refreshAppStateUiStatusOnly: ({
				taskList,
				containerType,
			}: {
				taskList: TaskList | undefined;
				containerType?: 'project' | 'plan';
			}) => Action<State, Props>;
			nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
			nextSetChecklistItemSeen: (taskId: string) => Action<State>;
			nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
			nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
			nextOpenTask: (
				taskId: string,
				{
					closeNudge,
				}?: {
					closeNudge?: boolean;
				},
			) => Action<State>;
			nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
			nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
			nextGetTasks: (props: {
				projectState: ProjectState | null;
				taskGroupParent: string;
				taskList?: TaskList;
				isEligibleForTailoredOnboardingM2P5?: boolean;
				teamType?: string;
				features: {
					hasBacklog?: boolean;
					hasSprint?: boolean;
					hasSummaryPage?: boolean;
					hasRoadmap?: boolean;
					hasList?: boolean;
					hasCalendar?: boolean;
					hasCode?: boolean;
					hasForms?: boolean;
					hasPages?: boolean;
					hasPlanDependencies?: boolean;
					hasPlanProgram?: boolean;
					hasPlanReleases?: boolean;
					hasPlanTeams?: boolean;
				};
				permissions?: {
					canCreateIssue?: boolean;
				};
				containerType?: 'project' | 'plan';
			}) => Action<State>;
			syncVisibilityWithLocal: () => Action<State>;
			universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
			setContainerType: (containerType: 'project' | 'plan') => Action<State>;
			setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
			setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
			focusQuickstartButton: () => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state: State) => state.containerType,
});
